import { tokens } from '../../theme/tokens';

const { surface, line, ink, accent, signal, radius, shadow, font } = tokens;

/**
 * Design tokens for the Brands & Vendors admin area.
 *
 * Originally its own palette and type scale from the design handoff. The
 * colour names are kept for the area's components; the values now come from
 * `theme/tokens.ts`, so brand health chips, stage pills and the rest of the
 * app use one accent and one signal set. The dense type scale (`bvType`) is
 * the area's own and stays.
 */

export const bvColor = {
  page: surface.page,
  surface: surface.card,
  surfaceTint: surface.tint,
  border: line.default,
  borderSubtle: line.hairline,
  borderWarm: signal.caution.border,
  ink: ink.primary,
  ink2: ink.body,
  ink3: ink.bodyAlt,
  ink4: ink.muted,
  muted: ink.muted,
  muted2: ink.faint,
  accent: accent.main,
  accentHover: accent.hover,
  accentTint: accent.border,
  accentTint2: line.strong,
  success: signal.positive.ink,
  successBorder: signal.positive.border,
  successBar: signal.positive.solid,
  warning: signal.caution.ink,
  warningBorder: signal.caution.border,
  warningBg: signal.caution.tint,
  warningBorder2: signal.caution.border,
  warningBar: signal.caution.solid,
  danger: signal.problem.ink,
  dangerBorder: signal.problem.border,
  dangerBg: signal.problem.tint,
  stageBarNeutral: signal.neutral.solid,
} as const;

/** Semantic constants used by the derived-state functions. */
export const OK = bvColor.success;
export const WARN = bvColor.warning;
export const BAD = bvColor.danger;
export const MUTE = bvColor.muted;

export const bvFont = font.sans;

export const bvRadius = {
  card: radius.card,
  panel: radius.card,
  control: radius.control,
  small: radius.small,
  xsmall: radius.chip,
  chip: radius.chip,
  pill: radius.pill,
  logoFrameLarge: radius.panel,
  logoFrameMedium: radius.panel,
  bar: 4,
} as const;

export const bvShadow = {
  card: shadow.card,
  logoFrame: '0 2px 4px rgba(17,24,39,0.05), 0 10px 26px rgba(17,24,39,0.06)',
  drawer: '-18px 0 44px rgba(17,24,39,0.16)',
  tooltip: '0 8px 22px rgba(17,24,39,0.22)',
} as const;

/** Type roles from the handoff. Sizes are intentionally non-round. */
export const bvType = {
  brandTitle: { fontSize: 31, fontWeight: 700, letterSpacing: '-0.022em' },
  dashTitle: { fontSize: 27, fontWeight: 700, letterSpacing: '-0.02em' },
  bigStat: { fontSize: 24, fontWeight: 700, letterSpacing: '-0.015em', fontVariantNumeric: 'tabular-nums' },
  h1: { fontSize: 22, fontWeight: 700, letterSpacing: '-0.015em' },
  peekTitle: { fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em' },
  signupTitle: { fontSize: 19, fontWeight: 700, letterSpacing: '-0.015em' },
  cardTitle: { fontSize: 14, fontWeight: 600 },
  tablePrimary: { fontSize: 13.5, fontWeight: 500 },
  body: { fontSize: 13.5, fontWeight: 400, lineHeight: 1.5, textWrap: 'pretty' as const },
  button: { fontSize: 13, fontWeight: 500 },
  buttonSmall: { fontSize: 12.5, fontWeight: 500 },
  tableSecondary: { fontSize: 12.5, fontWeight: 400 },
  caption: { fontSize: 11.5, fontWeight: 400 },
  microCaption: { fontSize: 11, fontWeight: 400 },
  eyebrow: {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: bvColor.muted,
  },
  stagePill: { fontSize: 11.5, fontWeight: 500 },
} as const;

export const tabular = { fontVariantNumeric: 'tabular-nums' } as const;

/** Page canvas padding: `24px 26px 40px` in the handoff. */
export const bvPageSx = {
  minHeight: '100%',
  fontFamily: bvFont,
  WebkitFontSmoothing: 'antialiased',
  px: '26px',
  pt: '24px',
  pb: '40px',
  color: bvColor.ink3,
} as const;

/** Content column caps at 1520px and centres. */
export const bvContentSx = { maxWidth: 1520, mx: 'auto' } as const;

export const bvCardSx = {
  bgcolor: bvColor.surface,
  border: `1px solid ${bvColor.border}`,
  borderRadius: `${bvRadius.card}px`,
  boxShadow: bvShadow.card,
  overflow: 'hidden',
} as const;

/** ~150ms ease on hover colour/border, matching the rest of the app. */
export const bvTransition = 'background-color 150ms ease, border-color 150ms ease, color 150ms ease';

export const formatCount = (value: number) => value.toLocaleString('en-US');
